import React, { useState, useEffect } from 'react';
import axios from 'axios';

function Dashboard({ token, apiBaseUrl }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const REFRESH_INTERVAL = 30000; // 30 seconds

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      }
      const response = await axios.get(`${apiBaseUrl}/admin/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setDashboardData(response.data);
      setLastUpdated(new Date());
      setError('');
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    fetchDashboardData(true);
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    const now = new Date();
    const diffSeconds = Math.floor((now - lastUpdated) / 1000);
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    return lastUpdated.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 text-center">{error}</div>;
  }

  const { beta_program, license_metrics } = dashboardData;

  // Calculate total lifetime licenses (all types are now lifetime)
  const totalLifetimeLicenses = (license_metrics?.beta_licenses || 0) +
    (license_metrics?.trial_licenses || 0) +
    (license_metrics?.professional_licenses || 0) +
    (license_metrics?.enterprise_licenses || 0);

  return (
    <div className="space-y-6">
      <div className="px-4 sm:px-0 flex justify-between items-start">
        <div>
          <h3 className="text-base font-semibold leading-7 text-gray-900">Admin Dashboard</h3>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
            Overview of license sales and user activity
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Auto-refresh</label>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${autoRefresh ? 'bg-blue-600' : 'bg-gray-200'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${autoRefresh ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {refreshing ? (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : null}
            Refresh
          </button>
          {lastUpdated && (
            <span className="text-xs text-gray-400">
              Updated {formatLastUpdated()}
            </span>
          )}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="text-lg font-medium text-gray-500">Total Licenses Sold</div>
              </div>
            </div>
            <div className="mt-1 text-3xl font-semibold text-gray-900">
              {totalLifetimeLicenses}
            </div>
            <div className="mt-1 text-sm text-gray-500">
              Lifetime licenses
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="text-lg font-medium text-gray-500">Active Users</div>
              </div>
            </div>
            <div className="mt-1 text-3xl font-semibold text-gray-900">
              {beta_program?.user_stats?.total_registered || 0}
            </div>
            <div className="mt-1 text-sm text-gray-500">
              Registered users
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="text-lg font-medium text-gray-500">Activations</div>
              </div>
            </div>
            <div className="mt-1 text-3xl font-semibold text-gray-900">
              {license_metrics?.total_activations || 0}
            </div>
            <div className="mt-1 text-sm text-gray-500">
              {license_metrics?.activations_today || 0} today
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="text-lg font-medium text-gray-500">Early Access Remaining</div>
              </div>
            </div>
            <div className="mt-1 text-3xl font-semibold text-gray-900">
              {150 - totalLifetimeLicenses}
            </div>
            <div className="mt-1 text-sm text-gray-500">
              of 150 licenses
            </div>
          </div>
        </div>
      </div>

      {/* Growth Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sales Growth</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div>
                <div className="text-sm font-medium text-blue-900">New Sales Today</div>
                <div className="text-2xl font-bold text-blue-600">{beta_program?.growth_metrics?.new_users_today || 0}</div>
              </div>
              <div className="text-blue-400">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                </svg>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <div className="text-sm font-medium text-green-900">New Sales This Week</div>
                <div className="text-2xl font-bold text-green-600">{beta_program?.growth_metrics?.new_users_week || 0}</div>
              </div>
              <div className="text-green-400">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
              <div>
                <div className="text-sm font-medium text-purple-900">Growth Rate</div>
                <div className="text-2xl font-bold text-purple-600">{beta_program?.growth_metrics?.growth_rate || 0}%</div>
              </div>
              <div className="text-purple-400">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* License Distribution */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">License Distribution</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 border-b">
              <span className="text-sm font-medium text-gray-500">Professional Licenses</span>
              <span className="text-lg font-semibold text-gray-900">{license_metrics?.professional_licenses || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 border-b">
              <span className="text-sm font-medium text-gray-500">Enterprise Licenses</span>
              <span className="text-lg font-semibold text-gray-900">{license_metrics?.enterprise_licenses || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3">
              <span className="text-sm font-medium text-gray-500">Early Access Licenses</span>
              <span className="text-lg font-semibold text-gray-900">{license_metrics?.beta_licenses || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
